import { useEffect, useRef, useCallback } from 'react';
import { Debouncer } from '@/utils/Debounce';

export const useVisibleProducts = (
  datalayerService,
  products,
  storeConf,
  categ,
  filterQuery,
  sort,
  searchMode
) => {
  const visibleProductsRef = useRef(new Set());
  const allViewedProductsRef = useRef(new Set());
  const sentProductsRef = useRef(new Set());
  const observerRef = useRef(null);
  const debouncerRef = useRef(null);
  const previousPathRef = useRef(categ?.urlKeyword || '');

  const sendToDataLayer = useCallback(() => {
    if (allViewedProductsRef.current.size === 0) return;

    const newProductIds = Array.from(allViewedProductsRef.current)
      .filter((productId) => !sentProductsRef.current.has(productId));

    if (newProductIds.length === 0) return;

    const newProductsData = newProductIds
      .map((productId) => products.find((p) => p._id === productId))
      .filter(Boolean);

    if (newProductsData.length > 0) {
      datalayerService.pushViewedProducts(
        newProductsData,
        filterQuery,
        sort,
        categ,
        searchMode,
        storeConf.storeId
      );

      newProductIds.forEach((productId) => {
        sentProductsRef.current.add(productId);
      });
    }
  }, [datalayerService, products, filterQuery, sort, categ, searchMode, storeConf.storeId]);

  useEffect(() => {
    const currentPath = categ?.urlKeyword || '';

    if (previousPathRef.current && previousPathRef.current !== currentPath) {
      debouncerRef.current?.cancel();
      sendToDataLayer();
      visibleProductsRef.current.clear();
      allViewedProductsRef.current.clear();
      sentProductsRef.current.clear();
    }

    previousPathRef.current = currentPath;
  }, [categ?.urlKeyword, sendToDataLayer]);

  useEffect(() => {
    debouncerRef.current = new Debouncer(sendToDataLayer, 500);
    return () => debouncerRef.current?.cancel();
  }, [sendToDataLayer]);

  const handleIntersection = useCallback((entries) => {
    const newProducts = entries
      .filter((entry) => entry.isIntersecting)
      .map((entry) => entry.target.dataset.productId)
      .filter((productId) => productId && !visibleProductsRef.current.has(productId));

    if (newProducts.length > 0) {
      newProducts.forEach((productId) => {
        visibleProductsRef.current.add(productId);
        allViewedProductsRef.current.add(productId);
      });
      debouncerRef.current?.run();
    }
  }, []);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: '0px',
      threshold: 0.5
    });

    return () => {
      observerRef.current?.disconnect();
      debouncerRef.current?.cancel();
    };
  }, [handleIntersection]);

  useEffect(() => {
    visibleProductsRef.current.clear();
    sentProductsRef.current.clear();
  }, [products]);

  const observeProduct = useCallback((element) => {
    observerRef.current?.observe(element);
  }, []);

  const unobserveProduct = useCallback((element) => {
    observerRef.current?.unobserve(element);
  }, []);

  return { observeProduct, unobserveProduct };
};
